import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface RotationSet {
  id: string;
  name: string;
  code: string;
  released_at: string | null;
}

export interface RotationInfo {
  core: RotationSet[];        // currently Core-legal
  archive: RotationSet[];      // rotated out of Core
  upcoming: RotationSet[];     // not released yet
  coreNames: Set<string>;
  archiveNames: Set<string>;
  nextToRotate: RotationSet | null; // oldest core set (rotates when next set releases)
  nextRelease: RotationSet | null;  // next upcoming set
}

/**
 * Lorcana Core = the four most recent released numbered sets.
 * Anything older is Archive; anything unreleased is Upcoming.
 */
export function useRotation() {
  return useQuery<RotationInfo>({
    queryKey: ['rotation-info'],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sets')
        .select('id,name,code,released_at')
        .not('released_at', 'is', null)
        .order('released_at', { ascending: true });
      if (error) throw error;
      const numbered = (data || []).filter((s: any) => /^\d+$/.test(s.code)) as RotationSet[];
      const today = Date.now();
      const released = numbered.filter(s => s.released_at && new Date(s.released_at).getTime() <= today);
      const upcoming = numbered.filter(s => s.released_at && new Date(s.released_at).getTime() > today);
      const core = released.slice(-4);
      const archive = released.slice(0, Math.max(0, released.length - 4));
      return {
        core,
        archive,
        upcoming,
        coreNames: new Set(core.map(s => s.name)),
        archiveNames: new Set(archive.map(s => s.name)),
        nextToRotate: core[0] || null,
        nextRelease: upcoming[0] || null,
      };
    },
  });
}
